import { useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "@gatsbyjs/reach-router";
import {
    APColumn,
    APText,
    APPalette,
    APButton,
    APFormFieldText,
    APFormFieldDropdown,
    getIt,
    handleErrorMessage,
    launchDialog,
    showSnackbar,
    Deferred,
} from "ap-components";
import { useWindowDimensions } from "ap-components/src/hooks/useWindowDimension";
import { AdvisorInfo } from "../WebStory/Models/WebStoryModel";
import PartnerApiServices from "../../Services/PartnerApiServices";
import AccountVerificationDialog from "./AccountVerificationDialog";
import { OnboardingCard, AdvisorInfoSection, SuccessView } from "./OnboardingChrome";

// =============================================================================
// Client Approval Page (partner-initiated signup)
//
// Same chrome as the referral onboarding page, but the client's details were
// already captured by the partner and arrive prefilled (and locked) inside the
// approval-link token. The client just reviews and taps Approve → OTP → signup.
// Figma: Client Onboarding › Approval link (node 1202:13858).
// =============================================================================

const MOBILE_BREAKPOINT = 640;

const TC_LINK_STYLE: React.CSSProperties = {
    color: APPalette["brand-400"],
    textDecoration: "underline",
    fontWeight: 500,
};

const CLIENT_TYPE_OPTIONS = [
    { value: "individual", label: "Individual" },
    { value: "minor", label: "Minor" },
    { value: "nre", label: "NRE" },
    { value: "nro", label: "NRO" },
];

/** Decoded `user` payload carried by the approval-link token (`{ path, user }`). */
interface ApprovalUser {
    panNumber?: string;
    phoneNumber?: string;
    email?: string;
    firstName?: string;
    guardianPan?: string;
    clientType?: "individual" | "minor" | "nre" | "nro";
    noIndianPhone?: boolean;
    subAdvisorId?: string;
}

function getSearchParam(key: string): string | null {
    return new URLSearchParams(window.location.search).get(key);
}

/**
 * Decode the JWT payload (base64url) WITHOUT verifying the signature — we only
 * need the embedded `user` to prefill the locked fields. The signup itself is
 * gated by the OTP, so a tampered token can't create an account.
 */
function decodeApprovalToken(tkn: string | null): ApprovalUser | null {
    if (!tkn) return null;
    try {
        const seg = tkn.split(".")[1];
        if (!seg) return null;
        const base64 = seg.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
        const json = JSON.parse(new TextDecoder().decode(bytes));
        return (json?.user ?? null) as ApprovalUser | null;
    } catch {
        return null;
    }
}

export default function ClientApprovalPage(_props: RouteComponentProps) {
    const tkn = getSearchParam("tkn");
    const productSource = getSearchParam("productSource");
    const user = useMemo(() => decodeApprovalToken(tkn), [tkn]);

    const [advisorData, setAdvisorData] = useState<AdvisorInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloadLink, setDownloadLink] = useState<string | null>(null);

    const { width } = useWindowDimensions();
    const isMobile = (width ?? 1024) < MOBILE_BREAKPOINT;

    useEffect(() => {
        loadAdvisorInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadAdvisorInfo() {
        if (!user?.subAdvisorId) {
            setLoading(false);
            return;
        }
        try {
            const info = await getIt(PartnerApiServices).getClientOnboardingAdvisorInfo(user.subAdvisorId);
            setAdvisorData(info);
        } catch (error) {
            handleErrorMessage(error);
        } finally {
            setLoading(false);
        }
    }

    const hasLogo = !!advisorData?.image_logo;

    if (!user) {
        return (
            <APColumn mainAxisAlignment="center" crossAxisAlignment="center" mainAxisSize="max" style={{ width: "100%" }}>
                <APText variant="h3" color={APPalette["negative-300"]}>
                    URL Link is not proper
                </APText>
            </APColumn>
        );
    }

    if (loading) {
        return (
            <APColumn mainAxisAlignment="center" crossAxisAlignment="center" mainAxisSize="max" style={{ width: "100%" }}>
                <APText variant="paragraph-large" color={APPalette["grey-500"]}>Loading...</APText>
            </APColumn>
        );
    }

    if (downloadLink) {
        return (
            <SuccessView
                advisorData={advisorData}
                hasLogo={hasLogo}
                isReferrerOnly={false}
                downloadLink={downloadLink}
                isMobile={isMobile}
                clientType={user.clientType ?? "individual"}
            />
        );
    }

    return (
        <ApprovalView
            user={user}
            advisorData={advisorData}
            hasLogo={hasLogo}
            isMobile={isMobile}
            productSource={productSource}
            tkn={tkn}
            onSuccess={setDownloadLink}
        />
    );
}

// =============================================================================
// Approval View — prefilled, locked fields + Approve CTA.
// =============================================================================

function ApprovalView({
    user,
    advisorData,
    hasLogo,
    isMobile,
    productSource,
    tkn,
    onSuccess,
}: {
    user: ApprovalUser;
    advisorData: AdvisorInfo | null;
    hasLogo: boolean;
    isMobile: boolean;
    productSource: string | null;
    tkn: string | null;
    onSuccess: (link: string) => void;
}) {
    const [submitting, setSubmitting] = useState(false);

    const isMinor = user.clientType === "minor";
    const emailMode = !!user.noIndianPhone; // NRE/NRO no-phone → link + OTP via email

    // TEMP: OTP bypassed — Approve hits the approve API directly and creates the user. Set false to restore OTP.
    const SKIP_OTP_VERIFICATION: boolean = true;

    async function handleApprove() {
        if (submitting) return;
        setSubmitting(true);
        try {
            if (SKIP_OTP_VERIFICATION) {
                const res = await getIt(PartnerApiServices).approveClientSignup(tkn ?? "", "");
                onSuccess(res.link);
                return;
            }

            await getIt(PartnerApiServices).generateSignupOTP(
                isMinor
                    ? { phoneNumber: user.phoneNumber, guardianPan: user.guardianPan, clientType: user.clientType }
                    : emailMode
                        ? { email: user.email, panNumber: user.panNumber, clientType: user.clientType }
                        : { phoneNumber: user.phoneNumber, panNumber: user.panNumber, clientType: user.clientType }
            );

            const link = await launchDialog<string>((deferred: Deferred<string>) => (
                <AccountVerificationDialog
                    deferred={deferred}
                    phoneNumber={emailMode ? "" : (user.phoneNumber ?? "")}
                    panNumber={isMinor ? undefined : user.panNumber}
                    guardianPan={isMinor ? user.guardianPan : undefined}
                    firstName={isMinor ? user.firstName : undefined}
                    email={emailMode ? user.email : undefined}
                    clientType={user.clientType}
                    subAdvisorId={user.subAdvisorId}
                    productSource={productSource ?? undefined}
                    tkn={tkn ?? undefined}
                />
            ));

            onSuccess(link);
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : "";
            if (msg && msg !== "USER_CANCELLED") showSnackbar(msg, "negative");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <OnboardingCard advisorData={advisorData} hasLogo={hasLogo} isMobile={isMobile}>
            <APColumn crossAxisAlignment="center" gap="24px" style={{ width: "100%" }}>
                {advisorData && <AdvisorInfoSection advisorData={advisorData} isMobile={isMobile} />}

                <APColumn crossAxisAlignment="stretch" gap="12px" style={{ width: "100%" }}>
                    {/* Prefilled + locked — the partner already captured these (token). */}
                    <APFormFieldDropdown
                        label="Client Type"
                        required
                        disabled
                        items={CLIENT_TYPE_OPTIONS}
                        initialValue={user.clientType ?? "individual"}
                        style={{ margin: 0, width: "100%" }}
                        hideCloseButton
                    />

                    {isMinor ? (
                        <>
                            <APFormFieldText label="Minor's Name" required disabled value={user.firstName ?? ""} style={{ margin: 0, width: "100%" }} />
                            <APFormFieldText label="Guardian PAN" required disabled value={user.guardianPan ?? ""} style={{ margin: 0, width: "100%" }} />
                            <APFormFieldText label="Phone number" required disabled value={user.phoneNumber ?? ""} style={{ margin: 0, width: "100%" }} />
                        </>
                    ) : (
                        <>
                            <APFormFieldText label="PAN" required disabled value={user.panNumber ?? ""} style={{ margin: 0, width: "100%" }} />
                            {emailMode
                                ? <APFormFieldText label="Email ID" required disabled value={user.email ?? ""} style={{ margin: 0, width: "100%" }} />
                                : <APFormFieldText label="Phone number" required disabled value={user.phoneNumber ?? ""} style={{ margin: 0, width: "100%" }} />}
                        </>
                    )}

                    {/* T&C line (static — Approve implies consent) */}
                    <APText variant="paragraph-xsmall" color={APPalette["grey-800"]}>
                        By clicking on{" "}
                        <b style={{ color: APPalette["brand-400"] }}>&quot;Approve&quot;</b>{" "}
                        button, you agree to our{" "}
                        <a href="https://www.assetplus.in/#!/info/tnc" style={TC_LINK_STYLE} target="_blank" rel="noopener noreferrer">
                            Terms &amp; Conditions
                        </a>{" "}
                        and{" "}
                        <a href="https://www.assetplus.in/#!/info/privacy" style={TC_LINK_STYLE} target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                        </a>
                    </APText>

                    {/* Approve button */}
                    <div style={{ width: "100%", paddingTop: "12px" }}>
                        <APButton
                            type="primary"
                            size="large"
                            style={{ borderRadius: "8px", backgroundColor: APPalette["brand-300"], width: "100%" }}
                            onClick={submitting ? undefined : async () => handleApprove()}
                        >
                            Approve
                        </APButton>
                    </div>
                </APColumn>
            </APColumn>
        </OnboardingCard>
    );
}
